import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from 'lucide-react';

const ImageLightbox = ({ images = [], initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [currentIndex]);

  const prev = () => {
    setZoom(1);
    setCurrentIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  };
  const next = () => {
    setZoom(1);
    setCurrentIndex((i) => (i < images.length - 1 ? i + 1 : 0));
  };

  if (!images.length) return null;
  const current = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50" onClick={(e) => e.stopPropagation()}>
        <span className="text-white/70 text-sm">
          {currentIndex + 1} / {images.length}
          {current?.filename && <span className="ml-3 text-white/50">{current.filename}</span>}
        </span>
        <div className="flex items-center gap-3">
          <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="text-white/70 hover:text-white p-1" data-testid="lightbox-zoom-out">
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-white/60 text-xs min-w-[40px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))} className="text-white/70 hover:text-white p-1" data-testid="lightbox-zoom-in">
            <ZoomIn className="w-5 h-5" />
          </button>
          {current?.url && (
            <a href={current.url} target="_blank" rel="noreferrer" className="text-white/70 hover:text-white p-1" data-testid="lightbox-download">
              <Download className="w-5 h-5" />
            </a>
          )}
          <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="lightbox-close">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center overflow-auto relative" onClick={(e) => e.stopPropagation()}>
        {images.length > 1 && (
          <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70 z-10" data-testid="lightbox-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        
        <img
          src={current?.url || current?.src || current}
          alt={current?.filename || `Image ${currentIndex + 1}`}
          className="max-h-full max-w-full object-contain transition-transform duration-200"
          style={{ transform: `scale(${zoom})` }}
          draggable={false}
          data-testid="lightbox-image"
        />
        
        {images.length > 1 && (
          <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70 z-10" data-testid="lightbox-next">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Thumbnail strip for multiple images */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 p-3 bg-black/50 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => { setCurrentIndex(i); setZoom(1); }}
              className={`w-12 h-12 rounded overflow-hidden border-2 flex-shrink-0 ${i === currentIndex ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
            >
              <img src={img?.url || img?.src || img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
